import React, { useState } from 'react';
import {
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonButton,
  IonIcon,
  IonChip,
  IonLabel
} from '@ionic/react';
import {
  bulb,
  chevronDown,
  chevronUp,
  informationCircle,
  sparkles
} from 'ionicons/icons';

import SVGIcon from './SVGIcon';
import AnimatedSVG from './AnimatedSVG';
import './ConceptRenderer.css';

/**
 * ConceptRenderer - Renders visual concept explanations
 * Used in lessons to display educational diagrams with explanations
 */
function ConceptRenderer({ concept }) {
  const [showDetails, setShowDetails] = useState(false);
  const [activeHotspot, setActiveHotspot] = useState(null);

  if (!concept) return null;

  const {
    title,
    description,
    visual,
    comparison,
    funFact,
    hotspots,
    keyPoints,
    examples
  } = concept;

  return (
    <div className="concept-renderer">
      {/* Main Concept Card */}
      <IonCard className="concept-card">
        <IonCardHeader>
          <IonCardTitle>
            <IonIcon icon={bulb} color="warning" />
            {title || 'Concept Explanation'}
          </IonCardTitle>
        </IonCardHeader>

        <IonCardContent>
          {/* Description */}
          {description && (
            <p className="concept-description">{description}</p>
          )}

          {/* Visual Diagram */}
          {visual && (
            <div className="concept-visual">
              <div className="visual-container">
                {visual.src.endsWith('.svg') ? (
                  <AnimatedSVG
                    src={visual.src}
                    alt={visual.alt || title}
                    className="concept-image"
                    animation={visual.animation || "fade-in"}
                    duration={visual.animationDuration || 2}
                  />
                ) : (
                  <img
                    src={`/assets/media/${visual.src}`}
                    alt={visual.alt || title}
                    className="concept-image"
                  />
                )}
                
                {/* Hotspots overlay */}
                {hotspots && hotspots.length > 0 && (
                  <div className="hotspots-overlay">
                    {hotspots.map((hotspot, idx) => (
                      <div
                        key={idx}
                        className={`hotspot ${activeHotspot === idx ? 'active' : ''}`}
                        style={{
                          left: `${hotspot.x}%`,
                          top: `${hotspot.y}%`
                        }}
                        onClick={() => setActiveHotspot(activeHotspot === idx ? null : idx)}
                      >
                        <span className="hotspot-number">{idx + 1}</span>
                        {activeHotspot === idx && (
                          <div className="hotspot-tooltip">
                            <strong>{hotspot.label}</strong>
                            <p>{hotspot.description}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {visual.caption && (
                <p className="visual-caption">{visual.caption}</p>
              )}
            </div>
          )}

          {/* Comparison Box */}
          {comparison && (
            <div className="comparison-box">
              <h4>
                <IonIcon icon={sparkles} />
                {comparison.title || 'Think of it like...'}
              </h4>
              <div className="comparison-content">
                <div className="comparison-item real-world">
                  <span className="comparison-label">Real World</span>
                  <p>{comparison.realWorld}</p>
                  {comparison.realWorldImage && (
                    <img 
                      src={`/assets/media/${comparison.realWorldImage}`} 
                      alt="Real world example"
                    />
                  )}
                </div>
                <div className="comparison-arrow">↔️</div>
                <div className="comparison-item code-world">
                  <span className="comparison-label">In Code</span>
                  <p>{comparison.code}</p>
                  {comparison.codeImage && (
                    <img 
                      src={`/assets/media/${comparison.codeImage}`} 
                      alt="Code example"
                    />
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Key Points */}
          {keyPoints && keyPoints.length > 0 && (
            <div className="key-points-section">
              <h4>Key Points:</h4>
              <ul className="key-points-list">
                {keyPoints.map((point, idx) => (
                  <li key={idx}>
                    <span className="key-point-bullet">✓</span>
                    {point}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Examples */}
          {examples && examples.length > 0 && (
            <div className="examples-section">
              <h4>Examples:</h4>
              {examples.map((example, idx) => (
                <div key={idx} className="example-item">
                  {example.label && <span className="example-label">{example.label}</span>}
                  {example.code && (
                    <pre className="example-code">{example.code}</pre>
                  )}
                  {example.explanation && (
                    <p className="example-explanation">{example.explanation}</p>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Fun Fact */}
          {funFact && (
            <div className="fun-fact-box">
              <IonChip color="tertiary" outline>
                <IonIcon icon={informationCircle} />
                <IonLabel>Fun Fact!</IonLabel>
              </IonChip>
              <p>{funFact}</p>
            </div>
          )}

          {/* Show More Details Button */}
          {(hotspots || examples) && (
            <IonButton 
              fill="clear" 
              expand="block"
              onClick={() => setShowDetails(!showDetails)}
              className="show-details-btn"
            >
              {showDetails ? 'Show Less' : 'Learn More'}
              <IonIcon 
                icon={showDetails ? chevronUp : chevronDown} 
                slot="end" 
              />
            </IonButton>
          )}

          {/* Expanded Details */}
          {showDetails && (
            <div className="expanded-details">
              {hotspots && hotspots.length > 0 && (
                <div className="hotspots-list">
                  <h4>Diagram Parts:</h4>
                  {hotspots.map((hotspot, idx) => (
                    <div 
                      key={idx} 
                      className={`hotspot-item ${activeHotspot === idx ? 'active' : ''}`}
                      onClick={() => setActiveHotspot(idx)}
                    >
                      <span className="hotspot-num">{idx + 1}</span>
                      <div className="hotspot-content">
                        <strong>{hotspot.label}</strong>
                        <p>{hotspot.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </IonCardContent>
      </IonCard>
    </div>
  );
}

export default ConceptRenderer;